import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';
import type { ReactNode } from 'react';

import { TableHead } from '@/components/ui/table';
import { cn } from '@/lib/utils';

/**
 * A `<TableHead>` that doubles as a sort control for DRF's `OrderingFilter`.
 *
 * The header cycles `none → asc → desc → none` on click. Only one column is
 * ever active across a table — clicking a *different* column resets the
 * previous one because the parent owns a single `ordering` string and we
 * derive direction by string equality.
 *
 * `column` is generic on the page's allowed-columns union so a typo can't
 * silently send an `ordering` value the backend rejects.
 *
 * a11y: the whole cell is a real `<button>` with `aria-sort` mirroring the
 * current direction, so screen readers announce sort state on focus.
 */
export interface SortableTableHeadProps<T extends string> {
  /** Column key this header controls (must be in the entity's allow-list). */
  column: T;
  /** Current `ordering` query param — `undefined` when no column is sorted. */
  value: string | undefined;
  /** Emits the next ordering value (or `undefined` to clear). */
  onChange: (next: string | undefined) => void;
  children: ReactNode;
  /** Extra classes forwarded to the underlying `<TableHead>`. */
  className?: string;
}

type Direction = 'asc' | 'desc' | 'none';

function directionFor<T extends string>(column: T, value: string | undefined): Direction {
  if (!value) return 'none';
  if (value === column) return 'asc';
  if (value === `-${column}`) return 'desc';
  return 'none';
}

function nextValue<T extends string>(column: T, current: Direction): string | undefined {
  // none → asc → desc → none. Returning `undefined` clears the sort entirely
  // so the caller can drop the param from the request.
  if (current === 'none') return column;
  if (current === 'asc') return `-${column}`;
  return undefined;
}

export function SortableTableHead<T extends string>({
  column,
  value,
  onChange,
  children,
  className,
}: SortableTableHeadProps<T>) {
  const direction = directionFor(column, value);
  const ariaSort =
    direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none';
  const Icon =
    direction === 'asc' ? ChevronUp : direction === 'desc' ? ChevronDown : ChevronsUpDown;
  // The sort button fills the cell, so it must mirror the cell's text alignment
  // (e.g. right-aligned money columns) — otherwise the label drifts from its
  // values. Centered headers are rare for sortable columns; left is the default.
  const alignRight = className?.includes('text-right');

  return (
    <TableHead aria-sort={ariaSort} className={cn('p-0', className)}>
      <button
        type="button"
        onClick={() => onChange(nextValue(column, direction))}
        className={cn(
          'hover:text-foreground flex h-10 w-full items-center gap-1.5 px-2 font-medium',
          alignRight ? 'justify-end text-right' : 'text-left',
        )}
      >
        <span>{children}</span>
        <Icon
          className={cn(
            'size-3.5 shrink-0',
            direction === 'none' ? 'text-muted-foreground' : 'text-foreground',
          )}
          aria-hidden
        />
      </button>
    </TableHead>
  );
}
